import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import * as z from "zod";
import { api } from "../http";
import { permissionSchema } from "./permissions";
import { paginatedListSchema } from "@/lib/utils/zod";

export const rolePermissionAssociationSchema = z.object({
  id: z.number(),
  role_id: z.number(),
  permission_id: z.number(),
  created_at: z.string().optional(),
  updated_at: z.string().optional(),
  deleted_at: z.string().nullable().optional(),
  permission: permissionSchema,
});

export const rolePermissionsListSchema = paginatedListSchema(
  rolePermissionAssociationSchema,
);

export const roleFormSchema = z.object({
  name: z.string().min(1, "Name is required"),
  description: z.string().min(1, "Description is required"),
});
export const roleSchema = roleFormSchema.extend({
  id: z.number(),
});

export const rolesListSchema = paginatedListSchema(roleSchema);

export type Role = z.infer<typeof roleSchema>;
export type RolesList = z.infer<typeof rolesListSchema>;
export type RoleFormData = z.infer<typeof roleFormSchema>;
export type RolePermissionAssociation = z.infer<
  typeof rolePermissionAssociationSchema
>;
export type RolePermissionsList = z.infer<typeof rolePermissionsListSchema>;

type PaginationParams = {
  page: number;
  per_page?: number;
};

export function rolesListQueryOptions(params: PaginationParams) {
  return queryOptions({
    queryKey: ["roles", "list", params],
    queryFn() {
      const searchParams = new URLSearchParams({
        page: params.page.toString(),
        per_page: (params.per_page ?? 10).toString(),
      });

      return api.raw
        .get("api/v1/roles/paginated", {
          searchParams,
        })
        .json<RolesList>();
    },
  });
}

export function roleQueryOptions(id: number) {
  return queryOptions({
    queryKey: ["roles", "detail", id],
    queryFn: async () => {
      const response = await api.raw.get(`api/v1/roles/${id}`).json<Role>();
      return response;
    },
  });
}

export function rolesAllQueryOptions() {
  return queryOptions({
    queryKey: ["roles", "all"],
    queryFn: async () => {
      const response = await api.raw.get("api/v1/roles/").json<Array<Role>>();
      return response;
    },
  });
}

type PermissionSearchParams = {
  page: number;
  per_page?: number;
};
export function rolePermissionsListQueryOptions(
  roleId: number,
  params: PermissionSearchParams,
) {
  return queryOptions({
    queryKey: ["roles", roleId, "permissions", "list", params],
    queryFn() {
      const searchParams = new URLSearchParams({
        page: params.page.toString(),
        per_page: (params.per_page ?? 10).toString(),
      });

      return api.raw
        .get(`api/v1/roles/${roleId}/permissions`, { searchParams })
        .json<RolePermissionsList>();
    },
  });
}

export function useRolesList(params: PaginationParams) {
  return useQuery(rolesListQueryOptions(params));
}

export function useRole(id: number) {
  return useQuery(roleQueryOptions(id));
}

export function useRolesAll() {
  return useQuery(rolesAllQueryOptions());
}

export function useRolePermissionsList(
  roleId: number,
  params: PermissionSearchParams,
) {
  return useQuery(rolePermissionsListQueryOptions(roleId, params));
}

export function useDeleteRolePermissionMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      roleId,
      associationId,
    }: {
      roleId: number;
      associationId: number;
    }) => {
      await api.raw.delete(
        `api/v1/roles/${roleId}/permissions/${associationId}`,
      );
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["roles", variables.roleId, "permissions", "list"],
      });
    },
  });
}
